// src/components/MembershipType.js
import React, { useState, useEffect } from 'react';
import { FaIdCard, FaInfoCircle } from 'react-icons/fa';
import MembershipModal from './MembershipModal';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';

// New, updated membership options
const membershipOptions = [
  {
    id: 'professional',
    name: 'Professional Member',
    price: 10,
    description: 'Open to practicing Respiratory Therapists and other qualified healthcare professionals in Africa.',
    benefits: [
      'Access to workshops, seminars, and monthly educational events (two per month across Africa)',
      'Early access and discounted rates for ARCA International Conference and major events',
      'Connect with respiratory therapists, pulmonologists, and critical care professionals across Africa',
      'Join committees, working groups, and leadership roles shaping respiratory care',
      'Access to career guidance, mentorship programs, and job opportunity postings',
      'Members-only access to clinical protocols, guidelines, training manuals and videos',
      'Eligibility for educational scholarships, research mini-grants, and professional development funding',
      'Reduced fees for conference attendance, trainings, and certification prep courses'
    ]
  },
  {
    id: 'associate',
    name: 'Associate Member',
    price: 15,
    description: 'Available to individuals in healthcare, education, or research fields who are interested in supporting respiratory care but are not practicing Respiratory Therapists.',
    benefits: [
      'Access to workshops, seminars, and monthly educational events',
      'Opportunities to earn continuing education credits (CEUs/CDEs)',
      'Discounted rates for ARCA International Conference and events',
      'Connect with respiratory care professionals and educators across Africa',
      'Opportunities to collaborate on advocacy and awareness initiatives',
      'Access to ARCA publications, newsletters, and research updates',
      'Participation in public health outreach and community screening programs',
      'Recognition as a supporter of respiratory health in Africa',
      'Reduced fees for conference attendance and training programs'
    ]
  },
  {
    id: 'international',
    name: 'International Member',
    price: 20,
    description: 'Open to Respiratory Therapists, educators, and healthcare professionals residing outside Africa.',
    benefits: [
      'Connection with African respiratory therapists and healthcare professionals',
      'Opportunities to contribute expertise to ARCA\'s growth and initiatives',
      'Access to digital educational resources, conferences, and webinars',
      'Networking across Africa and the global respiratory care diaspora',
      'Join committees and working groups shaping respiratory care in Africa',
      'Support for publishing research and speaking at conferences',
      'Access to ARCA publications, research updates, and online learning platforms',
      'Participation in collaborative research and training initiatives'
    ]
  },
  {
    id: 'student',
    name: 'Student Member',
    price: 5,
    description: 'Designed for students currently enrolled in respiratory therapy or related healthcare programs in Africa or abroad.',
    benefits: [
      'Reduced membership fee for students',
      'Access to workshops, seminars, and monthly educational events across Africa',
      'Discounted rates for ARCA International Conference and major events',
      'Access to mentorship programs with senior respiratory professionals',
      'Networking with peers and professionals across Africa and globally',
      'Tailored opportunities for early career growth and development',
      'Eligibility for educational scholarships and research mini-grants',
      'Members-only access to clinical protocols, training manuals and instructional videos',
      'Reduced fees for trainings and certification preparation courses'
    ]
  },
];

const MembershipType = ({ formData, handleChange, handleArrayChange, errors }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMembership, setSelectedMembership] = useState(null);

  useEffect(() => {
    const selection = membershipOptions.find(opt => opt.id === formData.membershipType);
    setSelectedMembership(selection);
  }, [formData.membershipType]);

  const getBorderClass = (field) => errors[field] ? 'border-danger' : 'border-secondary';
  const fadeIn = { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.5 } };
  const communicationPrefs = ['ARCA Newsletter', 'Event & webinar updates', 'Research updates', 'Partner marketing'];

  return (
    <motion.div {...fadeIn}>
      {/* Section Header */}
      <div className="d-flex align-items-center mb-4">
        <FaIdCard className="me-3" style={{ fontSize: '2rem', color: '#6B8E23' }} />
        <h2 className="h3 fw-semibold mb-0" style={{ color: '#6B8E23' }}>Membership Plan</h2>
      </div>

      {/* Membership Selection Card */}
      <div className="card border-0 shadow-sm mb-4" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="card-body p-4">
          <div className="row g-3">
            <div className="col-12">
              <label htmlFor="membershipType" className="form-label fw-medium">
                Membership Type <span className="text-danger">*</span>
              </label>
              <select
                id="membershipType"
                name="membershipType"
                value={formData.membershipType}
                onChange={(e) => {
                  const selectedType = e.target.value;
                  console.log('🏢 Membership type selected:', selectedType);
                  handleChange('membershipType', selectedType);


                }}
                className={`form-select form-select-lg ${getBorderClass('membershipType')}`}
              >
                <option value="">Choose a membership...</option>
                {membershipOptions.map(opt => (
                  <option key={opt.id} value={opt.id}>
                    {`${opt.name} ($${opt.price}/year)`}
                  </option>
                ))}
              </select>
              {errors.membershipType && (
                <div className="invalid-feedback d-block">{errors.membershipType}</div>
              )}

              {/* View Details Button */}
              <div className="text-end mt-2">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(true)}
                  disabled={!selectedMembership}
                  className="btn btn-link p-0 text-decoration-none d-inline-flex align-items-center"
                  style={{ color: selectedMembership ? '#6B8E23' : '#6c757d' }}
                >
                  <FaInfoCircle className="me-2" />View Details
                </button>
              </div>
            </div>

            <div className="col-md-6">
              <label htmlFor="membershipDuration" className="form-label fw-medium">
                Membership Duration <span className="text-danger">*</span>
              </label>
              <select
                id="membershipDuration"
                name="membershipDuration"
                value={formData.membershipDuration}
                onChange={(e) => handleChange('membershipDuration', e.target.value)}
                className="form-select"
                disabled
              >
                <option value="1 Year">1 Year</option>
              </select>
              <small className="text-muted d-block mt-1">All memberships are for 1 year duration</small>
            </div>
          </div>
        </div>
      </div>

      {/* Communication Preferences */}
      <div>
        <h3 className="h5 fw-medium mb-3" style={{ color: '#6B8E23' }}>Communication Preferences</h3>
        <div className="card border-0 shadow-sm" style={{ backgroundColor: '#f8f9fa' }}>
          <div className="card-body p-4">
            {communicationPrefs.map(item => (
              <div key={item} className="form-check mb-3">
                <input
                  id={item}
                  name="communication"
                  type="checkbox"
                  checked={formData.communication.includes(item)}
                  onChange={(e) => handleArrayChange('communication', item, e.target.checked)}
                  className="form-check-input"
                  style={{
                    borderColor: '#6B8E23',
                    backgroundColor: formData.communication.includes(item) ? '#6B8E23' : 'white'
                  }}
                />
                <label htmlFor={item} className="form-check-label fw-medium">
                  {item}
                </label>
              </div>
            ))}
          </div>
        </div>
      </div>

      <MembershipModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} membership={selectedMembership} />
    </motion.div>
  );
};
export default MembershipType;